import React, { useEffect, useMemo } from 'react';
import { Badge, Space, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import useForm, { FormContext } from '../../hooks/useForm';
import FormWrapper from '../FormWrapper';
import PosRetailersList from './PosRetailersList';
import PosSellersList from './PosSellersList';
import SaveButton from '../buttons/SaveButton';
import PageTitle from '../PageTitle';
import CancelButton from '../buttons/CancelButton';
import { PosTypes } from './constants';
import ShiftsList from './ShiftsList';
import usePermissions from '../../hooks/usePermission';
import PosUserTypesList from './PosUserTypesList';

const permissionsMap = {
  canEdit: ['SYS_SUPERUSER', 'TAXONOMIES_PERMISSION', 'HEAD_RETAILER'],
};

const EditPosForm = ({ pos, onSubmit, onCancel }) => {
  const { t } = useTranslation();

  const { canEdit } = usePermissions(permissionsMap);

  const dynamicUserTypeString = window._env_.DYNAMIC_USER_TYPE;
  const dynamicUserType = dynamicUserTypeString === 'true';

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t('errors.notEmpty')),
        address: yup.string().required(t('errors.notEmpty')),
        retailer: yup.number().required(t('errors.mustSelected')),
        type: yup.string().required(t('errors.mustSelected')),
        userTypes: dynamicUserType
          ? yup
              .array()
              .nullable()
              .min(1, t('errors.userTypeNotSelected'))
              .required('userTypes is required')
          : yup.array().nullable().notRequired(),
      }),
    [dynamicUserType, t],
  );

  const form = useForm({
    initialValue: pos,
    schema: validationSchema,
    validateOnChange: true,
    onSubmit,
    onCancel,
  });

  const { value, handleSubmit, input, setFormValue, select, errorMessages } =
    form;

  useEffect(() => {
    setFormValue(pos);
  }, [setFormValue, pos]);

  const { deleted } = value;

  return (
    <>
      <FormContext.Provider value={form}>
        <form onSubmit={handleSubmit}>
          <PageTitle title={pos.name || t('entity.taxonomy.poses._singular')} />
          <FormWrapper>
            <FormWrapper.Single>
              {value?.id && (
                <FormWrapper.Input
                  label="ID"
                  props={{ ...input('id'), disabled: true }}
                />
              )}
              <FormWrapper.Input
                label={t('entity.taxonomy.poses.name')}
                props={{
                  ...input('name'),
                  disabled: deleted || !canEdit,
                }}
              />

              <FormWrapper.Input
                label={t('entity.taxonomy.poses.address')}
                props={{
                  ...input('address'),
                  disabled: deleted || !canEdit,
                }}
              />

              <FormWrapper.Input
                label={t('entity.taxonomy.poses.workstation')}
                props={{
                  ...input('workstation'),
                  disabled: deleted || !canEdit,
                }}
              />

              <FormWrapper.Select
                label={t('entity.taxonomy.poses.type._')}
                props={{
                  options: PosTypes.content,
                  loading: PosTypes.loading,
                  disabled: deleted || !canEdit,
                  ...select('type'),
                  labelProp: (val) => t(`entity.taxonomy.poses.type.${val.id}`),
                }}
              />

              <Space style={{ paddingTop: '1rem' }}>
                {!deleted && canEdit && <SaveButton />}
                <CancelButton onCancel={onCancel} />
              </Space>
            </FormWrapper.Single>

            <FormWrapper.Double>
              <Tabs>
                {pos.id && (
                  <Tabs.TabPane
                    tab={t('pages.admin.taxonomies.poses.tabs.shifts._')}
                    key="shifts"
                  >
                    <ShiftsList
                      posId={value.id}
                      disable={value.deleted}
                      canEdit={canEdit}
                    />
                  </Tabs.TabPane>
                )}
                <Tabs.TabPane
                  style={{ position: 'static' }}
                  tab={
                    <span>
                      {t('pages.admin.taxonomies.poses.tabs.sellers')}
                    </span>
                  }
                  key="sellers"
                >
                  <PosSellersList canEdit={canEdit} />
                </Tabs.TabPane>
                <Tabs.TabPane
                  style={{ position: 'static' }}
                  tab={
                    <span>
                      {t('pages.admin.taxonomies.poses.tabs.retailers')}
                      {errorMessages && errorMessages.retailer && (
                        <Badge
                          count={1}
                          style={{ position: 'absolute', top: -25, right: -20 }}
                        />
                      )}
                    </span>
                  }
                  key="retailers"
                >
                  <PosRetailersList canEdit={canEdit} />
                </Tabs.TabPane>
                {dynamicUserType && (
                  <Tabs.TabPane
                    style={{ position: 'static' }}
                    tab={
                      <span>
                        {t('pages.admin.taxonomies.poses.tabs.userTypes')}
                        {errorMessages && errorMessages.userTypes && (
                          <Badge
                            count={1}
                            style={{
                              position: 'absolute',
                              top: -25,
                              right: -20,
                            }}
                          />
                        )}
                      </span>
                    }
                    key="userTypes"
                  >
                    <PosUserTypesList canEdit={canEdit} />
                  </Tabs.TabPane>
                )}
              </Tabs>
            </FormWrapper.Double>
          </FormWrapper>
        </form>
      </FormContext.Provider>
    </>
  );
};
export default EditPosForm;
